'use client';

import * as React from 'react';
import { MapPin, Users, Calendar, ChevronRight, GitCompare } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog';
import { Badge } from '@workspace/ui/components/badge';
import { Button } from '@workspace/ui/components/button';
import { Checkbox } from '@workspace/ui/components/checkbox';

import { type WeddingSampleDto } from '~/data/budget-explorer/get-public-wedding-samples';
import { WeddingDetailView } from './wedding-detail-view';
import { WeddingComparison } from './wedding-comparison';

type ViewMode = 'list' | 'detail' | 'compare';

interface WeddingListModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  weddings: WeddingSampleDto[];
  bucketRange: { min: number; max: number } | null;
}

export function WeddingListModal({
  open,
  onOpenChange,
  weddings,
  bucketRange,
}: WeddingListModalProps): React.JSX.Element {
  const [viewMode, setViewMode] = React.useState<ViewMode>('list');
  const [selectedWedding, setSelectedWedding] = React.useState<WeddingSampleDto | null>(null);
  const [compareSelection, setCompareSelection] = React.useState<Set<string>>(new Set());

  // Reset state when modal closes
  React.useEffect(() => {
    if (!open) {
      setViewMode('list');
      setSelectedWedding(null);
      setCompareSelection(new Set());
    }
  }, [open]);

  const handleViewDetail = (wedding: WeddingSampleDto) => {
    setSelectedWedding(wedding);
    setViewMode('detail');
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedWedding(null);
  };

  const handleToggleCompare = (weddingId: string) => {
    setCompareSelection((prev) => {
      const next = new Set(prev);
      if (next.has(weddingId)) {
        next.delete(weddingId);
      } else {
        next.add(weddingId);
      }
      return next;
    });
  };

  const handleStartCompare = () => {
    if (compareSelection.size >= 2) {
      setViewMode('compare');
    }
  };

  const handleRemoveFromCompare = (weddingId: string) => {
    setCompareSelection((prev) => {
      const next = new Set(prev);
      next.delete(weddingId);
      // If less than 2, go back to list
      if (next.size < 2) {
        setViewMode('list');
      }
      return next;
    });
  };

  const compareWeddings = React.useMemo(
    () => weddings.filter((w) => compareSelection.has(w.id)),
    [weddings, compareSelection]
  );

  const rangeLabel = bucketRange
    ? `$${(bucketRange.min / 1000).toFixed(0)}k - $${(bucketRange.max / 1000).toFixed(0)}k`
    : 'All Weddings';

  const getTitle = () => {
    switch (viewMode) {
      case 'detail':
        return 'Wedding Budget Details';
      case 'compare':
        return `Comparing ${compareSelection.size} Weddings`;
      default:
        return `Weddings: ${rangeLabel}`;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[85vh] flex flex-col p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl">{getTitle()}</DialogTitle>
              {viewMode === 'list' && (
                <p className="text-sm text-muted-foreground mt-1">
                  {weddings.length} wedding{weddings.length !== 1 ? 's' : ''} in this range
                  {compareSelection.size > 0 && (
                    <> • {compareSelection.size} selected for comparison</>
                  )}
                </p>
              )}
            </div>
            {viewMode === 'list' && compareSelection.size >= 2 && (
              <Button onClick={handleStartCompare} size="sm" className="gap-2">
                <GitCompare className="h-4 w-4" />
                Compare ({compareSelection.size})
              </Button>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6">
          <div className="py-4">
            {viewMode === 'detail' && selectedWedding && (
              <WeddingDetailView wedding={selectedWedding} onBack={handleBackToList} />
            )}
            {viewMode === 'compare' && (
              <WeddingComparison
                weddings={compareWeddings}
                onRemoveWedding={handleRemoveFromCompare}
                onBack={handleBackToList}
              />
            )}
            {viewMode === 'list' && (
              <WeddingList
                weddings={weddings}
                onSelect={handleViewDetail}
                compareSelection={compareSelection}
                onToggleCompare={handleToggleCompare}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface WeddingListProps {
  weddings: WeddingSampleDto[];
  onSelect: (wedding: WeddingSampleDto) => void;
  compareSelection: Set<string>;
  onToggleCompare: (weddingId: string) => void;
}

function WeddingList({
  weddings,
  onSelect,
  compareSelection,
  onToggleCompare,
}: WeddingListProps): React.JSX.Element {
  if (weddings.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No weddings found in this range.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground mb-3">
        Click a wedding to view details, or select multiple to compare side-by-side
      </p>
      {weddings.map((wedding, index) => (
        <WeddingCard
          key={wedding.id}
          wedding={wedding}
          index={index + 1}
          onClick={() => onSelect(wedding)}
          isSelected={compareSelection.has(wedding.id)}
          onToggleCompare={() => onToggleCompare(wedding.id)}
        />
      ))}
    </div>
  );
}

interface WeddingCardProps {
  wedding: WeddingSampleDto;
  index: number;
  onClick: () => void;
  isSelected: boolean;
  onToggleCompare: () => void;
}

function WeddingCard({
  wedding,
  index,
  onClick,
  isSelected,
  onToggleCompare,
}: WeddingCardProps): React.JSX.Element {
  // Calculate top spending categories
  const categoryTotals: Record<string, number> = {};
  for (const item of wedding.lineItems) {
    categoryTotals[item.category] = (categoryTotals[item.category] || 0) + item.amount;
  }

  const topCategories = Object.entries(categoryTotals)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  const CATEGORY_LABELS: Record<string, string> = {
    VENUE: 'Venue',
    CATERING: 'Catering',
    PHOTOGRAPHY: 'Photography',
    VIDEOGRAPHY: 'Videography',
    FLORALS: 'Florals',
    MUSIC: 'Music',
    ATTIRE: 'Attire',
    BEAUTY: 'Beauty',
    STATIONERY: 'Stationery',
    TRANSPORTATION: 'Transport',
    DECOR: 'Decor',
    RENTALS: 'Rentals',
    FAVORS: 'Favors',
    OFFICIANT: 'Officiant',
    WEDDING_PLANNER: 'Planner',
    OTHER: 'Other',
  };

  const perGuestCost = wedding.guestCount > 0
    ? Math.round(wedding.totalSpend / wedding.guestCount)
    : 0;

  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-lg border bg-card transition-all ${
        isSelected ? 'border-primary bg-primary/5' : 'hover:bg-accent/50'
      }`}
    >
      {/* Checkbox for comparison */}
      <div className="pt-1">
        <Checkbox
          checked={isSelected}
          onCheckedChange={onToggleCompare}
          aria-label={`Select wedding #${index} for comparison`}
        />
      </div>

      {/* Main content - clickable for details */}
      <button onClick={onClick} className="flex-1 text-left min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                #{index}
              </span>
              <span className="text-lg font-bold font-mono">
                ${wedding.totalSpend.toLocaleString()}
              </span>
            </div>

            {/* Meta info - compact */}
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mb-2">
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {wedding.location}
              </span>
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {wedding.guestCount}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {wedding.weddingYear}
              </span>
              <span>(${perGuestCost}/guest)</span>
            </div>

            {/* Top categories - compact */}
            <div className="flex flex-wrap gap-1.5">
              {topCategories.map(([category, amount]) => (
                <Badge
                  key={category}
                  variant="outline"
                  className="text-[10px] font-normal px-1.5 py-0"
                >
                  {CATEGORY_LABELS[category] || category}: ${amount.toLocaleString()}
                </Badge>
              ))}
              {Object.keys(categoryTotals).length > 3 && (
                <Badge
                  variant="outline"
                  className="text-[10px] font-normal px-1.5 py-0 text-muted-foreground"
                >
                  +{Object.keys(categoryTotals).length - 3}
                </Badge>
              )}
            </div>
          </div>

          {/* View details arrow */}
          <div className="flex items-center text-muted-foreground hover:text-primary transition-colors shrink-0">
            <ChevronRight className="h-4 w-4" />
          </div>
        </div>
      </button>
    </div>
  );
}

